import { useMutation, useQuery, useQueryClient } from 'react-query'

import { type ApiResponse, HTTPMethod, productRequest } from '../../global/api'
import {
  type PersonalVideoDTO,
  type PersonalVideoMetricsDTO,
} from '../model/PersonalVideo'
import {
  type PreviewVideoPageEmbedDTO,
  type PreviewVideoPagePostDTO,
} from '../model/PreviewVideoPage'
import {
  CACHE_TIME_10_MIN,
  CACHE_TIME_30_MIN,
} from '../components/video-details-page/constants'

const keys = {
  all: ['personal', 'videos'] as const,
  metrics: (videoId: number) =>
    ['personal', 'videos', videoId, 'metrics'] as const,
  tasks: (videoId: number) => ['personal', 'videos', videoId, 'tasks'] as const,
  embeds: (videoId: number) =>
    ['personal', 'videos', videoId, 'embeds'] as const,
}

const useGetVideoPreviewPageEmbeds = (videoId: number) => {
  return useQuery({
    queryKey: keys.embeds(videoId),
    queryFn: async () => {
      const response = await productRequest<
        ApiResponse<PreviewVideoPageEmbedDTO[]>
      >('personal', {
        url: '/api/2/player/embed-versions',
        method: HTTPMethod.GET,
        data: {
          object_id: videoId,
          object_type: 'photo',
        },
      })

      const embedLinks = response.data.filter((embed) => embed.type === 'email')
      const linkEmbed = response.data.find((embed) => embed.type === 'link')
      const pageLink = linkEmbed?.value.replace('/secret/', '/personal/') ?? ''

      return {
        embedLinks,
        pageLink,
      }
    },
    cacheTime: CACHE_TIME_30_MIN,
    staleTime: CACHE_TIME_10_MIN,
  })
}

type VideoWithMetrics = ApiResponse<PersonalVideoDTO[]> &
  PersonalVideoMetricsDTO &
  Partial<PreviewVideoPagePostDTO>

type VideoTaskDTO = {
  task_id: string
  task_photo_id: number
  task_part_uuid?: string
  assignee_user_id: number
  assignee_full_name: string
  assignee_avatar_url: string
  completed_p: number
}

type DuplicateVideoDTO = {
  photo_id: number
  uuid?: string
}

const useGetPersonalVideoWithMetricsQuery = (videoId: number) => {
  return useQuery({
    queryKey: keys.metrics(videoId),
    queryFn: async () => {
      const response = await productRequest<VideoWithMetrics>('personal', {
        url: '/api/2/photo/list',
        method: HTTPMethod.GET,
        data: {
          include_unpublished_p: 1,
          include_total_metrics_p: 1,
          include_photo_posts_p: 1,
          photo_id: videoId,
        },
      })

      if (!response.data.length) {
        return null
      }

      return {
        ...response,
        ...response.data[0],
      }
    },
    cacheTime: CACHE_TIME_10_MIN,
    staleTime: CACHE_TIME_10_MIN,
  })
}

const useGetVideoTasksQuery = (videoId: number) => {
  return useQuery({
    queryKey: keys.tasks(videoId),
    queryFn: async () => {
      const response = await productRequest<ApiResponse<VideoTaskDTO[]>>(
        'personal',
        {
          url: '/api/2/photo/mischung/task/list',
          method: HTTPMethod.GET,
          data: {
            task_photo_id: videoId,
          },
        }
      )

      return response.data
    },
    cacheTime: CACHE_TIME_10_MIN,
    staleTime: CACHE_TIME_10_MIN,
  })
}

type UpdateVideoDetailsMutationParams = {
  videoId: number
  title?: string
  description?: string
}

const useUpdateVideoDetailsMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      videoId,
      title,
      description,
    }: UpdateVideoDetailsMutationParams) => {
      await productRequest<ApiResponse<PersonalVideoDTO>>('personal', {
        url: '/api/2/photo/update',
        method: HTTPMethod.POST,
        data: {
          photo_id: videoId,
          ...(title !== undefined && { title }),
          ...(description !== undefined && { description }),
        },
      })
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries(keys.metrics(variables.videoId))

      const previousData = queryClient.getQueryData<
        PersonalVideoDTO & Partial<PreviewVideoPagePostDTO>
      >(keys.metrics(variables.videoId))

      if (previousData) {
        queryClient.setQueryData(keys.metrics(variables.videoId), {
          ...previousData,
          ...(variables.title !== undefined && { title: variables.title }),
          ...(variables.description !== undefined && {
            content: variables.description,
          }),
        })
      }

      return { previousData }
    },
    onError: (_err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          keys.metrics(variables.videoId),
          context.previousData
        )
      }
    },
    onSettled: (_, __, variables) => {
      void queryClient.invalidateQueries(keys.metrics(variables.videoId))
    },
  })
}

const useDeleteVideoMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (videoId: number) => {
      const response = await productRequest<ApiResponse<null>>('personal', {
        url: '/api/2/photo/delete',
        method: HTTPMethod.POST,
        data: {
          photo_id: videoId,
        },
      })

      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries(keys.all)
    },
  })
}

const useDuplicateVideoMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { videoId?: number; channelId?: number }) => {
      const response = await productRequest<ApiResponse<DuplicateVideoDTO>>(
        'personal',
        {
          url: '/api/2/photo/mischung/duplicate',
          method: HTTPMethod.POST,
          data: {
            ...(params.videoId && { photo_id: params.videoId }),
            ...(params.channelId && { user_group_id: params.channelId }),
            // Do not background the process when duplicating videos or flows.
            background_p: params.videoId || params.channelId ? 0 : 1,
          },
        }
      )

      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries(keys.all)
    },
  })
}

export {
  useGetPersonalVideoWithMetricsQuery,
  useGetVideoTasksQuery,
  useGetVideoPreviewPageEmbeds,
  useUpdateVideoDetailsMutation,
  useDeleteVideoMutation,
  useDuplicateVideoMutation,
}
